import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../context/I18nContext';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { acquireStream, releaseStream, isStreamLive, getPermissionState } from '../utils/cameraStream';
import { getSupermarketLogo } from '../utils/supermarketLogos';
import API_BASE_URL from '../config';
import './BarcodeScannerModal.css';

const getPlatform = () => window.Capacitor?.getPlatform?.() ?? 'web';

// ML Kit native scanner — Android only.
const isAndroidNative = () => getPlatform() === 'android';

const getNativeScanner = async () => {
  const { BarcodeScanner, BarcodeFormat } = await import('@capacitor-mlkit/barcode-scanning');
  return { BarcodeScanner, BarcodeFormat };
};

const formatPrice = (value) => `${(value || 0).toFixed(2)}€`;

const BarcodeScannerModal = ({ isOpen, onClose, onSearch }) => {
  const { t } = useI18n();
  const { addToCart } = useCart();
  const { isAuthenticated, authHeaders } = useAuth();
  const navigate = useNavigate();
  const html5ReaderId = 'barcode-html5-reader';
  const videoRef = useRef(null);
  const html5ContainerRef = useRef(null);
  const detectorRef = useRef(null);
  const animFrameRef = useRef(null);
  const html5QrRef = useRef(null);
  const scanHandledRef = useRef(false);
  const scanPausedRef = useRef(false);

  const [manualEan, setManualEan] = useState('');
  const [cameraError, setCameraError] = useState('');
  const [cameraReady, setCameraReady] = useState(false);
  const [useHtml5QrFallback, setUseHtml5QrFallback] = useState(false);
  const [permDenied, setPermDenied] = useState(false);
  const [nativeScanning, setNativeScanning] = useState(false);
  const [torchOn, setTorchOn] = useState(false);
  const [torchAvailable, setTorchAvailable] = useState(false);
  const [manualExpanded, setManualExpanded] = useState(false);

  // null | 'loading' | { product, prices, error, ean }
  const [scanMatch, setScanMatch] = useState(null);

  const stopCamera = async () => {
    if (animFrameRef.current) {
      cancelAnimationFrame(animFrameRef.current);
      animFrameRef.current = null;
    }
    detectorRef.current = null;
    if (html5QrRef.current) {
      try { await html5QrRef.current.stop(); } catch (_) {}
      try { await html5QrRef.current.clear(); } catch (_) {}
      html5QrRef.current = null;
    }
    if (videoRef.current) videoRef.current.srcObject = null;
    if (html5ContainerRef.current) html5ContainerRef.current.innerHTML = '';
    setCameraReady(false);
    setTorchOn(false);
    setTorchAvailable(false);
  };

  const toggleTorch = async () => {
    if (!videoRef.current?.srcObject) return;
    const track = videoRef.current.srcObject.getVideoTracks()[0];
    if (!track) return;
    try {
      await track.applyConstraints({ advanced: [{ torch: !torchOn }] });
      setTorchOn((v) => !v);
    } catch (_) {}
  };

  const fetchScanMatch = async (ean) => {
    setScanMatch('loading');
    try {
      const byEanRes = await axios.get(`${API_BASE_URL}/products/by-ean/${ean}`);
      const product = byEanRes.data;

      const groupRes = await axios.get(`${API_BASE_URL}/products/${product.id}/group`);
      const groupData = groupRes.data || {};
      // Extract a flat price-per-supermarket array from the group structure
      const prices = (groupData.products || []).flatMap((prod) =>
        (prod.prices || []).map((p) => ({
          supermarket: p.supermarket,
          supermarket_name: p.supermarket,
          price: p.price,
          price_per_unit: p.price_per_unit || null,
          supermarket_id: null,
        }))
      );

      setScanMatch({ product, prices, ean, error: null });

      // Record the scan: backend for logged-in users, localStorage fallback for guests.
      if (isAuthenticated) {
        axios.post(`${API_BASE_URL}/auth/me/scan`, {}, { headers: authHeaders }).catch(() => {});
      } else {
        try {
          const key = 'poupix_checked_count';
          const n = Math.max(0, parseInt(localStorage.getItem(key) || '0', 10));
          localStorage.setItem(key, String(n + 1));
        } catch (_) {}
      }
    } catch (_) {
      setScanMatch({ product: null, prices: [], ean, error: 'not_found' });
    }
  };

  const handleMatchAdd = async (product, priceInfo) => {
    await addToCart(product, priceInfo, 1);
    setScanMatch(null);
    scanPausedRef.current = false;
    scanHandledRef.current = false;
    onClose();
  };

  const handleMatchScanNext = () => {
    setScanMatch(null);
    scanPausedRef.current = false;
    scanHandledRef.current = false;
  };

  const handleMatchClose = () => {
    setScanMatch(null);
    scanPausedRef.current = false;
    scanHandledRef.current = false;
    onClose();
  };

  const handleMatchViewProduct = (product) => {
    setScanMatch(null);
    onClose();
    navigate(`/product/${product.id}`);
  };

  const handleMatchSearchAnyway = (ean) => {
    setScanMatch(null);
    scanPausedRef.current = false;
    onSearch(ean);
    onClose();
  };

  // ── Native path (Android) ──────────────────────────────────────────────
  useEffect(() => {
    if (!isOpen || !isAndroidNative()) return;
    let cancelled = false;
    const startNativeScan = async () => {
      try {
        const { BarcodeScanner, BarcodeFormat } = await getNativeScanner();
        const { camera } = await BarcodeScanner.requestPermissions();
        if (camera === 'denied') { setPermDenied(true); setCameraError(t('scanner.cameraPermDenied')); return; }
        setNativeScanning(true);
        const { barcodes } = await BarcodeScanner.scan({
          formats: [BarcodeFormat.Ean13, BarcodeFormat.Ean8, BarcodeFormat.UpcA, BarcodeFormat.UpcE],
        });
        if (cancelled) return;
        if (barcodes.length > 0) {
          const ean = barcodes[0].rawValue;
          if (/^\d{8,14}$/.test(ean)) {
            scanPausedRef.current = true;
            fetchScanMatch(ean);
          }
        }
      } catch (_) {
        if (!cancelled) setCameraError(t('scanner.cameraStartFail'));
      } finally {
        setNativeScanning(false);
      }
    };
    startNativeScan();
    return () => {
      cancelled = true;
      setNativeScanning(false);
      getNativeScanner().then(({ BarcodeScanner }) => BarcodeScanner.stopScan().catch(() => {})).catch(() => {});
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, t]);

  // ── Web path ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (isAndroidNative()) return;
    scanHandledRef.current = false;
    scanPausedRef.current = false;
    if (!isOpen) {
      stopCamera();
      setCameraError('');
      setManualEan('');
      setManualExpanded(false);
      setUseHtml5QrFallback(false);
      setPermDenied(false);
      setScanMatch(null);
      return;
    }
    setUseHtml5QrFallback(false);
    const hasCamera = !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
    if (!hasCamera) { setCameraError(t('scanner.noCamera')); return; }
    getPermissionState().then(state => {
      if (state === 'denied') { setPermDenied(true); setCameraError(t('scanner.cameraPermDenied')); }
    });
    let cancelled = false;
    const startCamera = async () => {
      try {
        const useNativeBarcodeDetector = 'BarcodeDetector' in window;
        if (!useNativeBarcodeDetector) {
          setUseHtml5QrFallback(true);
          const { Html5Qrcode } = await import('html5-qrcode');
          if (cancelled) return;
          if (!html5ContainerRef.current) { setCameraError(t('scanner.cameraInitFail')); return; }
          if (!isStreamLive()) await acquireStream({ video: { facingMode: 'environment' } });
          if (cancelled) return;
          const scanner = new Html5Qrcode(html5ReaderId, { verbose: false });
          html5QrRef.current = scanner;
          await scanner.start(
            { facingMode: 'environment' },
            { fps: 10, qrbox: { width: 240, height: 120 }, aspectRatio: 1.7778 },
            (decodedText) => {
              const value = (decodedText || '').trim();
              if (/^\d{8,14}$/.test(value) && !scanHandledRef.current && !scanPausedRef.current) {
                scanHandledRef.current = true;
                scanPausedRef.current = true;
                fetchScanMatch(value);
              }
            },
            () => {}
          );
          setCameraReady(true);
          return;
        }
        setUseHtml5QrFallback(false);
        if (!videoRef.current) return;
        const stream = await acquireStream({
          video: { facingMode: { ideal: 'environment' }, width: { ideal: 1280 }, height: { ideal: 720 } },
        });
        if (cancelled) { if (videoRef.current) videoRef.current.srcObject = null; return; }
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
        }
        // Check torch support after getting stream
        const track = stream.getVideoTracks()[0];
        const caps = track?.getCapabilities?.();
        setTorchAvailable(!!caps?.torch);

        detectorRef.current = new window.BarcodeDetector({ formats: ['ean_13', 'ean_8', 'upc_a', 'upc_e'] });
        setCameraReady(true);
        const detect = async () => {
          if (!videoRef.current || videoRef.current.readyState < 2) {
            animFrameRef.current = requestAnimationFrame(detect); return;
          }
          try {
            if (!scanPausedRef.current) {
              const codes = await detectorRef.current.detect(videoRef.current);
              if (codes.length > 0 && !scanHandledRef.current) {
                const ean = codes[0].rawValue;
                scanHandledRef.current = true;
                scanPausedRef.current = true;
                fetchScanMatch(ean);
                return;
              }
            }
          } catch (_) {}
          animFrameRef.current = requestAnimationFrame(detect);
        };
        animFrameRef.current = requestAnimationFrame(detect);
      } catch (err) {
        if (!cancelled) {
          if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
            setPermDenied(true); setCameraError(t('scanner.cameraPermDenied'));
          } else {
            setCameraError(t('scanner.cameraStartFail'));
          }
        }
      }
    };
    startCamera();
    return () => { cancelled = true; stopCamera(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, t]);

  useEffect(() => {
    if (isAndroidNative()) return;
    const onPageHide = () => releaseStream();
    window.addEventListener('pagehide', onPageHide);
    return () => window.removeEventListener('pagehide', onPageHide);
  }, []);

  useEffect(() => {
    if (isAndroidNative()) return;
    const onVisibility = () => {
      if (!videoRef.current) return;
      if (document.visibilityState === 'hidden') videoRef.current.pause();
      else if (cameraReady) videoRef.current.play().catch(() => {});
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [cameraReady]);

  if (!isOpen) return null;

  const handleManualSearch = (e) => {
    e.preventDefault();
    const ean = manualEan.trim();
    if (ean) {
      scanPausedRef.current = true;
      fetchScanMatch(ean);
    }
  };

  // ── Android native waiting state ───────────────────────────────────────
  if (isAndroidNative()) {
    return (
      <div className="scanner-fs">
        <div className="scanner-fs-dark-bg" />
        <div className="scanner-fs-topbar">
          <button className="scanner-fs-glass-btn" onClick={onClose} aria-label={t('scanner.close')}>
            <svg viewBox="0 0 24 24" fill="none"><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
          </button>
          <div className="scanner-fs-instruction">{t('scanner.title')}</div>
          <div className="scanner-fs-glass-btn scanner-fs-glass-btn--ghost" aria-hidden="true" />
        </div>
        <div className="scanner-fs-center-msg">
          {cameraError ? <p>{cameraError}</p> : <p>{nativeScanning ? t('scanner.cameraHint') : t('scanner.cameraStartFail')}</p>}
        </div>
        <div className="scanner-fs-bottom">
          <div className="scanner-fs-handle" />
          {manualExpanded ? (
            <form className="scanner-fs-manual-form" onSubmit={handleManualSearch}>
              <input
                type="text" inputMode="numeric" className="scanner-fs-manual-input"
                placeholder={t('scanner.manualPlaceholder')} autoFocus
                value={manualEan} onChange={(e) => setManualEan(e.target.value)}
              />
              <button type="submit" className="scanner-fs-manual-submit">{t('scanner.manualSearch')}</button>
            </form>
          ) : (
            <div className="scanner-fs-bottom-btns">
              <button className="scanner-fs-entry-btn" onClick={() => setManualExpanded(true)}>
                <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
                  <circle cx="6" cy="6" r="1.4"/><circle cx="12" cy="6" r="1.4"/><circle cx="18" cy="6" r="1.4"/>
                  <circle cx="6" cy="12" r="1.4"/><circle cx="12" cy="12" r="1.4"/><circle cx="18" cy="12" r="1.4"/>
                  <circle cx="6" cy="18" r="1.4"/><circle cx="12" cy="18" r="1.4"/><circle cx="18" cy="18" r="1.4"/>
                </svg>
                Introduzir EAN
              </button>
            </div>
          )}
        </div>
        {scanMatch && (
          <ScanMatchSheet
            scanMatch={scanMatch}
            onAdd={handleMatchAdd}
            onScanNext={handleMatchScanNext}
            onClose={handleMatchClose}
            onSearchAnyway={handleMatchSearchAnyway}
            onViewProduct={handleMatchViewProduct}
          />
        )}
      </div>
    );
  }

  // ── Web full-screen scanner ────────────────────────────────────────────
  return (
    <div className="scanner-fs" role="dialog" aria-modal="true" aria-label={t('scanner.title')}>

      {/* Live camera — full-screen background */}
      <video
        ref={videoRef}
        className={`scanner-fs-video${useHtml5QrFallback ? ' scanner-fs-video--hidden' : ''}`}
        playsInline muted
      />
      <div
        id={html5ReaderId}
        ref={html5ContainerRef}
        className={`scanner-fs-html5${useHtml5QrFallback ? ' scanner-fs-html5--active' : ''}`}
      />

      {/* Dark backdrop when camera unavailable */}
      {!cameraReady && <div className="scanner-fs-dark-bg" />}

      {/* Glass top bar */}
      <div className="scanner-fs-topbar">
        <button className="scanner-fs-glass-btn" onClick={onClose} aria-label={t('scanner.close')}>
          <svg viewBox="0 0 24 24" fill="none">
            <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        </button>
        <div className="scanner-fs-instruction">Aponte ao código de barras</div>
        {torchAvailable ? (
          <button
            className={`scanner-fs-glass-btn${torchOn ? ' scanner-fs-glass-btn--active' : ''}`}
            onClick={toggleTorch} aria-label="Lanterna" aria-pressed={torchOn}
          >
            <svg viewBox="0 0 24 24" fill="none" width="22" height="22">
              <path d="M7 2h10l-2 7h-6L7 2zM8 9h8v3l-3 10h-2L8 12V9z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/>
            </svg>
          </button>
        ) : (
          <div className="scanner-fs-glass-btn scanner-fs-glass-btn--ghost" aria-hidden="true" />
        )}
      </div>

      {/* Error state overlay */}
      {cameraError && (
        <div className="scanner-fs-error-wrap">
          <div className="scanner-fs-error-icon">📷</div>
          <p className="scanner-fs-error-msg">{cameraError}</p>
          {permDenied && <p className="scanner-fs-error-hint">{t('scanner.cameraPermHint')}</p>}
        </div>
      )}

      {/* Reticle (only when camera is working and no match sheet) */}
      {!cameraError && !scanMatch && (
        <>
          {/* Semi-dark scrim around reticle via box-shadow trick */}
          <div className="scanner-fs-reticle-host">
            <div className="scanner-fs-reticle">
              {/* Corner brackets */}
              <div className="scanner-fs-corner scanner-fs-corner--tl" />
              <div className="scanner-fs-corner scanner-fs-corner--tr" />
              <div className="scanner-fs-corner scanner-fs-corner--bl" />
              <div className="scanner-fs-corner scanner-fs-corner--br" />
              {/* Animated laser */}
              <div className="scanner-fs-laser" />
            </div>
          </div>
          <p className="scanner-fs-hint">Mantenha o código direito a ~10 cm da câmara</p>
        </>
      )}

      {/* Glass bottom sheet (manual entry) — hidden when match sheet is shown */}
      {!scanMatch && (
        <div className="scanner-fs-bottom">
          <div className="scanner-fs-handle" />
          {manualExpanded ? (
            <form className="scanner-fs-manual-form" onSubmit={handleManualSearch}>
              <input
                type="text" inputMode="numeric" className="scanner-fs-manual-input"
                placeholder={t('scanner.manualPlaceholder')} autoFocus
                value={manualEan} onChange={(e) => setManualEan(e.target.value)}
              />
              <button type="submit" className="scanner-fs-manual-submit">{t('scanner.manualSearch')}</button>
            </form>
          ) : (
            <div className="scanner-fs-bottom-btns">
              <button className="scanner-fs-entry-btn" onClick={() => setManualExpanded(true)}>
                <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
                  <circle cx="6" cy="6" r="1.4"/><circle cx="12" cy="6" r="1.4"/><circle cx="18" cy="6" r="1.4"/>
                  <circle cx="6" cy="12" r="1.4"/><circle cx="12" cy="12" r="1.4"/><circle cx="18" cy="12" r="1.4"/>
                  <circle cx="6" cy="18" r="1.4"/><circle cx="12" cy="18" r="1.4"/><circle cx="18" cy="18" r="1.4"/>
                </svg>
                Introduzir EAN
              </button>
            </div>
          )}
        </div>
      )}

      {/* Scan match bottom sheet — overlaid on camera */}
      {scanMatch && (
        <ScanMatchSheet
          scanMatch={scanMatch}
          onAdd={handleMatchAdd}
          onScanNext={handleMatchScanNext}
          onClose={handleMatchClose}
          onSearchAnyway={handleMatchSearchAnyway}
          onViewProduct={handleMatchViewProduct}
        />
      )}
    </div>
  );
};

/* ── ScanMatchSheet component ── */
const ScanMatchSheet = ({ scanMatch, onAdd, onScanNext, onClose, onSearchAnyway, onViewProduct }) => {
  if (scanMatch === 'loading') {
    return (
      <div className="smatch-sheet">
        <div className="smatch-handle" />
        <div className="smatch-loading">
          <div className="smatch-spinner" />
          <p className="smatch-loading-text">A identificar produto...</p>
        </div>
      </div>
    );
  }

  const { product, prices, ean, error } = scanMatch;

  if (error || !product) {
    return (
      <div className="smatch-sheet">
        <div className="smatch-handle" />
        <div className="smatch-not-found">
          <div className="smatch-not-found-icon">🔍</div>
          <p className="smatch-not-found-title">Produto não encontrado</p>
          <p className="smatch-not-found-ean">{ean}</p>
          <div className="smatch-not-found-actions">
            <button className="smatch-btn-search" onClick={() => onSearchAnyway(ean)}>
              Pesquisar na lista
            </button>
            <button className="smatch-btn-scan-icon" onClick={onScanNext} aria-label="Scan outro produto">
              <svg viewBox="0 0 24 24" fill="none" width="20" height="20">
                <path d="M3 9V6a1 1 0 011-1h3M21 9V6a1 1 0 00-1-1h-3M3 15v3a1 1 0 001 1h3M21 15v3a1 1 0 01-1 1h-3" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                <path d="M7 12h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Find best price
  const sortedPrices = [...prices].sort((a, b) => {
    const pa = a.price ?? a.current_price ?? Infinity;
    const pb = b.price ?? b.current_price ?? Infinity;
    return pa - pb;
  });
  const bestEntry = sortedPrices[0];
  const bestPrice = bestEntry ? (bestEntry.price ?? bestEntry.current_price) : null;
  const bestSupermarket = bestEntry ? (bestEntry.supermarket_name || bestEntry.supermarket || '') : '';

  const bestPriceInfo = bestEntry
    ? {
        price: bestPrice,
        supermarket: bestSupermarket,
        supermarket_id: bestEntry.supermarket_id || null,
      }
    : { price: product.price || 0, supermarket: '', supermarket_id: null };

  return (
    <div className="smatch-sheet">
      <div className="smatch-handle" />

      {/* Header row */}
      <div className="smatch-header">
        <div className="smatch-img-wrap">
          {product.image_url ? (
            <img src={product.image_url} alt={product.name} className="smatch-img" />
          ) : (
            <div className="smatch-img-placeholder">
              <svg viewBox="0 0 24 24" fill="none" width="22" height="22">
                <rect x="3" y="3" width="18" height="18" rx="3" stroke="currentColor" strokeWidth="1.5"/>
                <circle cx="8.5" cy="8.5" r="1.5" fill="currentColor"/>
                <path d="M21 15l-5-5L5 21" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
          )}
        </div>
        <div className="smatch-product-info">
          <p className="smatch-product-name">{product.name}</p>
          <code className="smatch-ean">{ean}</code>
        </div>
        <button className="smatch-close-btn" onClick={onClose} aria-label="Fechar">
          ✕
        </button>
      </div>

      {/* Identified badge */}
      <div className="smatch-identified-badge">
        <svg viewBox="0 0 24 24" fill="none" width="14" height="14">
          <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
        Identificado
      </div>

      {/* Price comparison rows */}
      {sortedPrices.length > 0 && (
        <div className="smatch-price-list">
          {sortedPrices.map((entry, idx) => {
            const storeName = entry.supermarket_name || entry.supermarket || 'Loja';
            const price = entry.price ?? entry.current_price;
            const logo = getSupermarketLogo(storeName);
            const isBest = idx === 0;
            return (
              <div
                key={`${storeName}-${idx}`}
                className={`smatch-price-row${isBest ? ' smatch-price-row--best' : ''}`}
              >
                <div className="smatch-price-store-left">
                  {logo ? (
                    <img src={logo.src} alt="" aria-hidden="true" className="smatch-store-logo" />
                  ) : (
                    <span className="smatch-store-name-fallback">{storeName}</span>
                  )}
                </div>
                <span className={`smatch-price-value${isBest ? ' smatch-price-value--best' : ''}`}>
                  {formatPrice(price)}
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* Savings tile */}
      {sortedPrices.length > 1 && bestPrice !== null && (() => {
        const worstPrice = sortedPrices[sortedPrices.length - 1].price ?? sortedPrices[sortedPrices.length - 1].current_price ?? bestPrice;
        const savings = worstPrice - bestPrice;
        if (savings <= 0) return null;
        return (
          <div className="smatch-savings-tile">
            <span>Poupa {formatPrice(savings)} no {bestSupermarket}</span>
          </div>
        );
      })()}

      {/* Action buttons */}
      <div className="smatch-actions">
        <button
          className="smatch-btn-add"
          onClick={() => onAdd(product, bestPriceInfo)}
        >
          Adicionar à lista
        </button>
        <button className="smatch-btn-scan-icon" onClick={onScanNext} aria-label="Scan próximo produto">
          <svg viewBox="0 0 24 24" fill="none" width="20" height="20">
            <path d="M3 9V6a1 1 0 011-1h3M21 9V6a1 1 0 00-1-1h-3M3 15v3a1 1 0 001 1h3M21 15v3a1 1 0 01-1 1h-3" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            <path d="M7 12h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        </button>
      </div>

      <button className="smatch-view-product" onClick={() => onViewProduct(product)}>
        Ver página do produto
        <svg viewBox="0 0 24 24" fill="none" width="14" height="14">
          <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>
    </div>
  );
};

export default BarcodeScannerModal;
